import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { watchKeyboard } from './lib/keyboard';
import './styles/tokens.css';
import './styles/base.css';
import './styles/components.css';

/* The service worker, on the web only.
 *
 * On the website it is what lets the app open offline and install to a home
 * screen. Inside the phone and desktop shells it does nothing useful — the files
 * are already inside the app — and one thing harmful: after an update it kept
 * handing out the previous build, so a successful install changed nothing on
 * screen. There, any worker left from before is removed along with its caches,
 * and the page reloads once so what shows is what was installed. The book is in
 * IndexedDB and is not touched by any of this. */
function isNativeShell(): boolean {
  const w = window as unknown as { Capacitor?: { isNativePlatform?: () => boolean }; __TAURI_INTERNALS__?: unknown };
  return Boolean(w.Capacitor?.isNativePlatform?.()) || '__TAURI_INTERNALS__' in w
    || location.protocol === 'tauri:';
}

if ('serviceWorker' in navigator) {
  if (isNativeShell()) {
    void (async () => {
      const registrations = await navigator.serviceWorker.getRegistrations().catch(() => []);
      if (registrations.length === 0) return;
      await Promise.all(registrations.map((r) => r.unregister()));
      const keys = await caches?.keys().catch(() => []) ?? [];
      await Promise.all(keys.map((k) => caches.delete(k)));
      // Once per session, so a worker that somehow comes back cannot loop the page.
      if (!sessionStorage.getItem('sw-cleared')) {
        sessionStorage.setItem('sw-cleared', '1');
        location.reload();
      }
    })();
  } else {
    window.addEventListener('load', () => {
      void navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => {});
    });
  }
}

// The tab bar hides while the on-screen keyboard is up. See lib/keyboard.ts.
watchKeyboard();

const root = document.getElementById('root');
if (!root) throw new Error('No #root element');

createRoot(root).render(
  <StrictMode>
    <App />
  </StrictMode>,
);
